import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, Mic, Minus, Play, Plus, X } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChordPicker } from '@/components/ChordPicker';
import { LevelWave } from '@/components/LevelWave';
import { Button, Card, Segmented, SectionTitle } from '@/components/ui';
import { BPM_RANGE } from '@/config/audio';
import { BEATS_OPTIONS, DEFAULTS, DURATION_OPTIONS } from '@/config/training';
import { PROGRESSIONS } from '@/data/progressions';
import { t } from '@/i18n';
import { useMicrophone, useMicrophoneLease } from '@/hooks/useAudio';
import { usePreferences } from '@/hooks/usePreferences';
import type { BeatsPerChord, Recommendation } from '@/types/training';
import './SetupPage.css';

/**
 * Chord-change setup — five decisions, no more.
 *
 * Everything the previous version exposed (window sizes, thresholds, detection
 * modes) lives in `src/config` now. A beginner cannot make those choices well,
 * and asking them to is how the last version became unusable.
 */
export function ChordChangeSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { prefs, update } = usePreferences();
  const { state, enable } = useMicrophone();
  useMicrophoneLease();

  const recommendation = (location.state as { recommendation?: Recommendation } | null)?.recommendation;

  const [chordIds, setChordIds] = useState<string[]>(
    () => recommendation?.chordIds ?? prefs.lastChordIds ?? [...DEFAULTS.starterChords],
  );
  const [bpm, setBpm] = useState(() => recommendation?.bpm || prefs.lastBpm);
  const [beatsPerChord, setBeatsPerChord] = useState<BeatsPerChord>(
    () => (prefs.lastBeatsPerChord as BeatsPerChord) ?? DEFAULTS.beatsPerChord,
  );
  const [durationMin, setDurationMin] = useState(
    () => recommendation?.durationMin ?? prefs.lastDurationMin,
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  // One suggested chain, chosen for how well it fits what is already selected.
  const suggestion = useMemo(() => {
    const chosen = new Set(chordIds);
    const scored = PROGRESSIONS.map((progression) => ({
      progression,
      overlap: progression.chordIds.filter((id) => chosen.has(id)).length,
    })).sort((a, b) => b.overlap - a.overlap);
    return scored[0]?.progression ?? PROGRESSIONS[0];
  }, [chordIds]);

  const micReady = state.status === 'ready';
  const canStart = chordIds.length >= 2 && micReady;

  const toggleChord = (chordId: string) => {
    setChordIds((current) =>
      current.includes(chordId) ? current.filter((id) => id !== chordId) : [...current, chordId],
    );
  };

  const start = () => {
    update({
      lastChordIds: chordIds,
      lastBpm: bpm,
      lastBeatsPerChord: beatsPerChord,
      lastDurationMin: durationMin,
      onboardingComplete: true,
    });
    navigate('/training/chord-change/run', {
      state: {
        settings: {
          chordIds,
          bpm,
          beatsPerChord,
          durationMin,
          clickEnabled: prefs.clickEnabled,
          adaptiveTempo: prefs.adaptiveTempo,
        },
      },
    });
  };

  return (
    <AppShell title={t('modes.chordChange')} showBack>
      <section className="page-section">
        <SectionTitle id="chords-label">{t('setup.chords')}</SectionTitle>
        <ul className="chord-chips" aria-labelledby="chords-label">
          {chordIds.map((chordId) => (
            <li key={chordId}>
              <button
                type="button"
                className="chord-chip"
                onClick={() => toggleChord(chordId)}
                aria-label={t('setup.removeChord', { chord: chordId })}
              >
                <span>{chordId}</span>
                <X size={14} aria-hidden="true" className="chord-chip__remove" />
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="chord-chip chord-chip--add"
              onClick={() => setPickerOpen(true)}
              aria-label={t('setup.addChord')}
            >
              <Plus size={22} aria-hidden="true" />
            </button>
          </li>
        </ul>
        {chordIds.length < 2 && <p className="setup__warning">{t('setup.needTwoChords')}</p>}
      </section>

      <section className="page-section">
        <SectionTitle>{t('setup.progression')}</SectionTitle>
        <button
          type="button"
          className="progression-card"
          onClick={() => setChordIds([...suggestion.chordIds])}
        >
          <span className="progression-card__chain">
            {suggestion.chordIds.map((chordId, index) => (
              <span key={`${chordId}-${index}`} className="progression-card__step">
                {index > 0 && <span className="progression-card__arrow">→</span>}
                <span className="progression-card__chord">{chordId}</span>
              </span>
            ))}
          </span>
          <span className="progression-card__hint">{suggestion.hintRu}</span>
        </button>
      </section>

      <section className="page-section">
        <div className="setup__label-row">
          <SectionTitle>{t('setup.tempo')}</SectionTitle>
          <span className="setup__value tnum">{t('setup.bpm', { value: bpm })}</span>
        </div>
        <div className="slider-row">
          <button
            type="button"
            className="btn btn--icon"
            onClick={() => setBpm((value) => Math.max(BPM_RANGE.min, value - 5))}
            aria-label={t('setup.tempoDown')}
          >
            <Minus size={20} aria-hidden="true" />
          </button>
          <input
            className="slider"
            type="range"
            min={BPM_RANGE.min}
            max={BPM_RANGE.max}
            step={BPM_RANGE.step}
            value={bpm}
            onChange={(event) => setBpm(Number(event.target.value))}
            aria-label={t('setup.tempo')}
            aria-valuetext={t('setup.bpm', { value: bpm })}
            style={
              {
                '--fill': `${((bpm - BPM_RANGE.min) / (BPM_RANGE.max - BPM_RANGE.min)) * 100}%`,
              } as React.CSSProperties
            }
          />
          <button
            type="button"
            className="btn btn--icon"
            onClick={() => setBpm((value) => Math.min(BPM_RANGE.max, value + 5))}
            aria-label={t('setup.tempoUp')}
          >
            <Plus size={20} aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="page-section">
        <SectionTitle>{t('setup.beatsPerChord')}</SectionTitle>
        <Segmented
          label={t('setup.beatsPerChord')}
          options={BEATS_OPTIONS}
          value={beatsPerChord}
          onChange={(value) => setBeatsPerChord(value as BeatsPerChord)}
        />
      </section>

      <section className="page-section">
        <SectionTitle>{t('setup.duration')}</SectionTitle>
        <Segmented
          label={t('setup.duration')}
          options={DURATION_OPTIONS}
          value={durationMin}
          onChange={setDurationMin}
          format={(value) => t('setup.durationOption', { count: value })}
        />
      </section>

      <section className="page-section">
        <Card>
          <div className="mic-check">
            <span className={`mic-check__badge${micReady ? ' mic-check__badge--ready' : ''}`}>
              <Mic size={22} aria-hidden="true" />
            </span>
            <div className="mic-check__body">
              <p className="mic-check__title">{t('mic.check')}</p>
              {micReady ? (
                <p className="mic-check__status">
                  <CheckCircle2 size={16} aria-hidden="true" />
                  {t('mic.ready')}
                </p>
              ) : (
                <button type="button" className="mic-check__action" onClick={() => void enable()}>
                  {t('mic.enable')}
                </button>
              )}
            </div>
            {micReady && <LevelWave bars={18} height={26} />}
          </div>
          {/* Tuning is the one thing worth doing before every session, and an
              out-of-tune guitar reads as bad playing in every mode. One quiet
              line, not a card. */}
          <Link to="/tuner" className="mic-check__tuner">
            {t('setup.tuneFirst')}
          </Link>
        </Card>
      </section>

      <Button large block disabled={!canStart} onClick={start}>
        <Play size={20} fill="currentColor" aria-hidden="true" />
        {t('setup.start')}
      </Button>

      {/* Mounted only while open, so it starts fresh and the focus trap has
          something to trap. */}
      {pickerOpen && (
        <ChordPicker
          selected={chordIds}
          onToggle={toggleChord}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </AppShell>
  );
}
